import { Flex, Grid, Stack, Switch, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { PortalButton } from 'components/Button/PortalButton';
import { CenteredLoading } from 'components/CenteredLoading';
import { FormSwitch } from 'components/Form/FormSwitch';
import { PaperHeader } from 'components/PaperHeader/PaperHeader';
import { useGroupReportConfiguration } from 'hooks/api/useGroups';
import { useTranslation } from 'react-i18next';
import { BsFloppy } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import { GroupReportConfiguration } from 'models/report';
import { queryKeys } from 'utils/query';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import { updateGroupReportConfiguration } from 'services/groups';
import { ReportContentForm } from 'components/Form/ReportContentForm';
import { MAX_REPORTS_TOTAL } from 'utils/general';
import { useState } from 'react';
import { initialReportContentSetting } from 'utils/reports';

export const GroupReportsForm = () => {
  const { t } = useTranslation();
  const { groupId } = useParams<{ groupId: string; tab?: string }>();
  const { data, isLoading } = useGroupReportConfiguration(groupId);
  const [isFormPopulated, setFormPopulated] = useState(false);

  const { mutate: updateConfiguration, isPending } = useCustomMutation({
    fn: updateGroupReportConfiguration,
    onSuccess: {
      invalidateQueryKeys: [[queryKeys.groupReportConfiguration, groupId]],
      message: t('group_settings_successfully_updated'),
    },
  });

  const form = useForm<GroupReportConfiguration>({
    mode: 'uncontrolled',
    validate: {
      reportsTotal: (value) => (value > MAX_REPORTS_TOTAL || value < 0 ? t('validations_reports_total') : null),
    },
    onValuesChange: (values) => {
      form.setFieldValue(
        'reportsTotal',
        Object.values(values.reportContent).reduce((sum, v) => sum + Number(v), 0),
      );
    },
  });

  if (!isFormPopulated && data) {
    const initialData = { ...data, reportContent: { ...initialReportContentSetting, ...data.reportContent } };
    form.initialize(initialData);
    setFormPopulated(true);
  }

  if (isLoading) return <CenteredLoading />;

  return (
    <form onSubmit={form.onSubmit((values) => updateConfiguration(values))}>
      <PaperHeader titleKey={'group_reports_tab'} iconKey={'groupsettings_general_reports_tooltip'} />
      <Stack mt={20}>
        <Title order={6}>{t('group_form_subtitle')}</Title>
        <Grid gutter={10}>
          <FormSwitch label={t('group_prescription_triggers_enabled_label')}>
            <Switch {...form.getInputProps('prescriptionTriggers.prescriptionEnded', { type: 'checkbox' })} />
          </FormSwitch>
          <FormSwitch label={t('group_prescription_triggers_weekly_label')}>
            <Switch {...form.getInputProps('prescriptionTriggers.7PeriodDaysPassed', { type: 'checkbox' })} />
          </FormSwitch>
          <FormSwitch label={t('group_prescription_triggers_monthly_label')}>
            <Switch {...form.getInputProps('prescriptionTriggers.30PeriodDaysPassed', { type: 'checkbox' })} />
          </FormSwitch>
        </Grid>
        <Title order={6}>{t('content_label')}</Title>
        <ReportContentForm form={form} disabled={!form.getValues().id} />
        <Flex justify={'flex-end'}>
          <PortalButton
            type={'submit'}
            label={t('save_label')}
            Icon={BsFloppy}
            loading={isPending}
            disabled={
              !form.getValues().id ||
              form.getValues().reportsTotal <= 0 ||
              form.getValues().reportsTotal > MAX_REPORTS_TOTAL
            }
          />
        </Flex>
      </Stack>
    </form>
  );
};
